/**
 * The web→app hand-off rides an ordinary content URL as `?h=<token>`: the
 * Live Link appends it to its open-in-app link for a signed-in visitor, and
 * whichever build opens the URL — the installed app through the universal
 * link, or the web app after the edge redirect — reads it off, strips it, and
 * signs in before showing the content. Pure string helpers, shared by the
 * native intent handler and the web landing handler.
 */
const PARAM = "h";

/** The hand-off token carried by a URL or a bare query string, or null. */
export function readHandoffToken(urlOrSearch: string): string | null {
  const search = searchOf(urlOrSearch);
  if (!search) return null;
  const value = new URLSearchParams(search).get(PARAM);
  return value &&
    /^(?:[A-Za-z0-9_-]{1,128}\.)?[A-Za-z0-9_-]{16,256}$/.test(value)
    ? value
    : null;
}

/**
 * The user a token names, when the mint prefixed it (`<uid>.<random>`), so a
 * signed-in app can tell without exchanging whether the token is its own
 * user's. Null for tokens minted without the hint.
 */
export function handoffTokenUser(token: string): string | null {
  const dot = token.indexOf(".");
  return dot === -1 ? null : token.slice(0, dot);
}

/** The same URL or path without its hand-off token; other params kept. */
export function stripHandoffToken(urlOrPath: string): string {
  const hashIndex = urlOrPath.indexOf("#");
  const hash = hashIndex === -1 ? "" : urlOrPath.slice(hashIndex);
  const withoutHash =
    hashIndex === -1 ? urlOrPath : urlOrPath.slice(0, hashIndex);
  const queryIndex = withoutHash.indexOf("?");
  if (queryIndex === -1) return urlOrPath;
  const params = new URLSearchParams(withoutHash.slice(queryIndex + 1));
  params.delete(PARAM);
  const query = params.toString();
  return `${withoutHash.slice(0, queryIndex)}${query ? `?${query}` : ""}${hash}`;
}

function searchOf(value: string): string {
  if (value.startsWith("?")) return value;
  const withoutHash = value.split("#")[0] ?? "";
  const queryIndex = withoutHash.indexOf("?");
  return queryIndex === -1 ? "" : withoutHash.slice(queryIndex);
}

/**
 * The receiver route for a token found on a content URL: the hand-off screen
 * signs in and hands the stripped destination on as `next`.
 */
export function handoffRoute(token: string, next: string): string {
  const params = new URLSearchParams({ h: token, next });
  return `/auth/handoff?${params.toString()}`;
}
